import { useCallback, useEffect, useState, type SubmitEvent } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router";
import { readFirstAdministratorSetupOpen, readRegistrationStatus } from "../../api/accountsApi";
import { appPaths, HOME_PATH, type ReturnAfterSignIn } from "../../shared/routing/appPaths";
import { isProblemWithCode, toUiError } from "../../errors/uiError";
import ErrorNotice from "../../errors/ErrorNotice";
import { signInDestination, signInMayOpenGuide } from "../connectors/setup/afterSignIn";
import { loadConnectorSetup } from "../connectors/setup/useConnectorSetup";
import { useSession } from "../../session/sessionContext";
import { useLoad } from "../../shared/loading/useLoad";
import { useSubmission } from "../../shared/state/useSubmission";
import AppButton from "../../ui/buttons/AppButton";
import LanguageThemeControls from "../../ui/controls/LanguageThemeControls";
import StatusMessage from "../../ui/feedback/StatusMessage";
import { FORM_CONTROL_CLASS } from "../../ui/styles/formStyles";
import AuthPageSwitcher from "./AuthPageSwitcher";
import { companionLoginPathOf, resolverLoginPathOf } from "./companionLogin";
import Field from "../../ui/forms/Field";

function returnPathOf(state: unknown): string {
  const returnTo = (state as Partial<ReturnAfterSignIn> | null)?.returnTo;
  return typeof returnTo === "string" && returnTo.startsWith("/") ? returnTo : HOME_PATH;
}

/**
 * Sign in with email and password. New accounts come from invitations, the first-administrator
 * setup, or self-registration where the server allows it (Q-36).
 */
export default function LoginForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { session, signIn } = useSession();
  const { state: submission, submit } = useSubmission();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const returnPath = returnPathOf(location.state);
  const companionLoginPath = companionLoginPathOf(location.search) ?? resolverLoginPathOf(location.search);
  const submitting = submission.status === "submitting";
  const loadSetupOpen = useCallback((signal: AbortSignal) => readFirstAdministratorSetupOpen(signal), []);
  const { state: setupOpen } = useLoad(loadSetupOpen);
  const loadRegistration = useCallback((signal: AbortSignal) => readRegistrationStatus(signal), []);
  const { state: registration } = useLoad(loadRegistration);

  useEffect(() => {
    if (session.status !== "signedIn") return;
    if (companionLoginPath !== null) {
      // The sign-in bridge for a companion app (ADR-0024): the server finishes the OIDC request.
      window.location.assign(companionLoginPath);
      return;
    }
    if (!signInMayOpenGuide(returnPath)) {
      void navigate(returnPath, { replace: true });
      return;
    }
    // The first sign-in opens the connector setup guide once (OB-5); a failed read never holds the sign-in up.
    const isAdministrator = session.session.account.accountRole === "administrator";
    const controller = new AbortController();
    loadConnectorSetup(isAdministrator, controller.signal).then(
      (setup) => { if (!controller.signal.aborted) void navigate(signInDestination(returnPath, setup), { replace: true }); },
      () => { if (!controller.signal.aborted) void navigate(returnPath, { replace: true }); },
    );
    return () => { controller.abort(); };
  }, [companionLoginPath, navigate, returnPath, session]);

  const handleSubmit = (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();
    submit(async () => {
      try { await signIn({ email, password }); }
      catch (caught) {
        const error = toUiError(caught);
        if (error.kind === "problem" && isProblemWithCode(error, "account_pending")) {
          setPassword("");
          // The page says what the account waits for, as the server said (F133).
          await navigate(appPaths.waitingApproval(), { state: { waitsFor: error.problem.details["waits_for"] } });
          return;
        }
        throw caught;
      }
    });
  };

  return (
    <div className="app-surface-muted app-auth-shell">
      <div className="app-auth-toolbar app-auth-panel-width">
        <AuthPageSwitcher to="/" direction="left" label={t("app.toLanding")} className="shrink-0" compact={true} />
        <LanguageThemeControls compact={true} showLabels={false} showSymbols={true} slim={true} subtle={true} layout="inline" />
      </div>
      <div className="app-auth-panel-wrap app-auth-panel-width">
        <form onSubmit={handleSubmit} className="app-card w-full p-6 shadow-sm">
          <h1 className="app-title mb-4 text-xl font-semibold">{t("login.title")}</h1>

          {session.status === "expired" ? (
            <StatusMessage tone="info" className="mb-4">{t("login.sessionEnded")}</StatusMessage>
          ) : null}
          {setupOpen.status === "loaded" && setupOpen.value ? (
            <StatusMessage tone="info" title={t("login.setupOpenTitle")} className="mb-4">
              <p>{t("login.setupOpenMessage")}</p>
              <Link to={appPaths.firstAdministratorSetup()} className="app-link mt-2 inline-block underline">
                {t("login.setupOpenAction")}
              </Link>
            </StatusMessage>
          ) : null}

          <fieldset disabled={submitting}>
            <Field id="login-email" label={t("login.email")}>
              {(control) => (
                <input
                  {...control}
                  type="email"
                  className={`mb-3 ${FORM_CONTROL_CLASS}`}
                  value={email}
                  onChange={(event) => {
                    setEmail(event.target.value);
                  }}
                  autoComplete="username"
                  required
                />
              )}
            </Field>

            <Field id="login-password" label={t("login.password")}>
              {(control) => (
                <input
                  {...control}
                  type="password"
                  className={`mb-3 ${FORM_CONTROL_CLASS}`}
                  value={password}
                  onChange={(event) => {
                    setPassword(event.target.value);
                  }}
                  autoComplete="current-password"
                  required
                />
              )}
            </Field>
            <div className="mb-3 text-right">
              <Link to={appPaths.forgotPassword()} className="app-link text-sm underline">
                {t("login.forgotPassword")}
              </Link>
            </div>

            {submission.status === "failed" ? <ErrorNotice error={submission.error} className="mb-3" /> : null}

            <AppButton type="submit" className="w-full" variant="primary">
              {submitting ? t("login.pleaseWait") : t("login.submit")}
            </AppButton>

            {registration.status === "loaded" && registration.value.selfRegistration !== "off" ? (
              <div className="mt-4 text-sm">
                {t("login.noAccount")}{" "}
                <Link to={appPaths.register()} className="app-link underline">
                  {t("login.register")}
                </Link>
              </div>
            ) : null}
            <div className="mt-2 text-sm">
              {t("login.haveInvitation")}{" "}
              <Link to={appPaths.invitationAcceptance()} className="app-link underline">
                {t("login.acceptInvitation")}
              </Link>
            </div>
          </fieldset>
        </form>
      </div>
    </div>
  );
}
